import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, CloudDownload, AlertTriangle, PauseCircle, ShieldOff, X, Loader2, FileArchive, Mail } from "lucide-react";

const STATUS = {
  scannen: { label: "Map scannen…", cls: "bg-blue-100 text-blue-800", icon: Loader2, spin: true },
  gescand: { label: "Klaar om te starten", cls: "bg-slate-100 text-slate-700", icon: FileArchive },
  bezig: { label: "Importeren…", cls: "bg-blue-100 text-blue-800", icon: Loader2, spin: true },
  gepauzeerd: { label: "Gepauzeerd", cls: "bg-amber-100 text-amber-800", icon: PauseCircle },
  klaar: { label: "Import klaar", cls: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  fout: { label: "Gestopt met fout", cls: "bg-rose-100 text-rose-800", icon: AlertTriangle },
};
const gb = (b) => (b >= 1e9 ? `${(b / 1e9).toFixed(1)} GB` : `${Math.round((b || 0) / 1e6)} MB`);
const n = (x) => Number(x || 0).toLocaleString("nl-NL");
const geleden = (iso) => {
  if (!iso) return "—";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 1 ? "zojuist" : m < 60 ? `${m} min geleden` : m < 1440 ? `${Math.floor(m / 60)} u ${m % 60} min geleden` : `${Math.floor(m / 1440)} d geleden`;
};
const LOOPT = ["scannen", "bezig", "gescand", "gepauzeerd"];

export function WorkdriveImportKaart() {
  const [d, setD] = useState(null);
  const [weg, setWeg] = useState(false);
  const vorige = useRef(null);

  useEffect(() => {
    let stop = false, timer;
    const laad = async () => {
      try {
        const { data } = await api.get("/mail-export/workdrive/voortgang");
        if (stop) return;
        const w = data.wachtrij;
        if (w && vorige.current && LOOPT.includes(vorige.current) && w.status === "klaar") toast.success(`Mailhistorie-import klaar: ${w.aantal_klaar} van ${w.aantal} bestanden verwerkt`);
        vorige.current = w?.status || null;
        setD(data);
        timer = setTimeout(laad, w && LOOPT.includes(w.status) ? 30000 : 180000);
      } catch { if (!stop) timer = setTimeout(laad, 120000); }
    };
    laad();
    return () => { stop = true; clearTimeout(timer); };
  }, []);

  const w = d?.wachtrij;
  if (!w || weg || d?.import_afgesloten || localStorage.getItem(`wd_import_dicht_${w.id}`) === "1") return null;
  const st = STATUS[w.status] || STATUS.gescand;
  const Icon = st.icon;
  const t = w.tellers || {};
  const pctBestanden = w.aantal ? Math.round((w.aantal_klaar / w.aantal) * 100) : 0;
  const pctBytes = w.totaal_bytes ? Math.min(100, Math.round((w.bytes_klaar / w.totaal_bytes) * 100)) : pctBestanden;
  const huidigPct = w.huidig?.bytes ? Math.round(((w.huidig.gedownload || 0) / w.huidig.bytes) * 100) : null;
  const afgerond = w.status === "klaar" || w.status === "fout";
  const sluit = () => { localStorage.setItem(`wd_import_dicht_${w.id}`, "1"); setWeg(true); };
  const match = d.match?.laatste;
  const dm = d.directe_match;
  const rand = w.status === "klaar" ? "border-emerald-300" : w.status === "fout" ? "border-rose-300" : w.stil ? "border-amber-300" : "border-blue-200";

  return (
    <div className={`rounded-xl border bg-white p-4 ${rand}`} data-testid="workdrive-import-kaart">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${st.cls}`}><Icon className={`h-5 w-5 ${st.spin ? "animate-spin" : ""}`} /></div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading font-bold text-slate-900">Mailhistorie-import (WorkDrive)</h3>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${st.cls}`} data-testid="wd-status">{st.label}</span>
              {w.stil && !w.pauze_venster && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" data-testid="wd-stil">stilgevallen — wordt automatisch hervat</span>}
              {w.pauze_venster && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-100 text-sky-800" data-testid="wd-pauzevenster">gepauzeerd tot {w.pauze_venster.tot_nl} — {w.pauze_venster.reden || "pauzevenster"}</span>}
            </div>
            <div className="text-xs text-slate-500 truncate">{w.naam}{w.gestart_op ? ` · gestart ${new Date(w.gestart_op).toLocaleString("nl-NL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}` : ""}{w.hervat_aantal ? ` · ${w.hervat_aantal}× hervat` : ""}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {d.noodstop_aan ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1 rounded-md" title={`Sinds de start: ${n(d.mail_sinds_start?.verzonden)} verzonden, ${n(d.mail_sinds_start?.geblokkeerd_noodstop)} geblokkeerd`} data-testid="wd-noodstop">
              <ShieldOff className="h-3.5 w-3.5" /> Noodstop aan · {n(d.mail_sinds_start?.verzonden)} mails verzonden
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-md" data-testid="wd-mail-aan"><Mail className="h-3.5 w-3.5" /> Mailen staat aan</span>
          )}
          {afgerond && <button onClick={sluit} className="text-slate-400 hover:text-slate-700 p-1 rounded hover:bg-slate-100" title="Kaart sluiten" data-testid="wd-sluiten"><X className="h-4 w-4" /></button>}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-4">
        <div className="space-y-2">
          <div className="flex items-baseline justify-between text-sm">
            <span className="font-semibold text-slate-800"><FileArchive className="h-3.5 w-3.5 inline mr-1 text-slate-400" />{w.aantal_klaar} van {w.aantal} zips klaar</span>
            <span className="text-xs text-slate-500">{gb(w.bytes_klaar)} / {gb(w.totaal_bytes)} · {pctBytes}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden" data-testid="wd-voortgang-balk">
            <div className={`h-full rounded-full transition-[width] duration-700 ${w.status === "fout" ? "bg-rose-500" : w.status === "klaar" ? "bg-emerald-500" : "bg-[hsl(var(--brand-blue))]"}`} style={{ width: `${pctBytes}%` }} />
          </div>
          {w.tempo && w.status === "bezig" && (
            <div className="text-xs text-slate-600" data-testid="wd-tempo">
              ⚡ {w.tempo.mb_per_min} MB/min · {w.tempo.mails_per_min} mails/min
              {w.tempo.eta_min != null && <> · klaar over ≈ <b>{w.tempo.eta_min >= 90 ? `${(w.tempo.eta_min / 60).toFixed(1)} uur` : `${w.tempo.eta_min} min`}</b></>}
              <span className="text-slate-400"> (o.b.v. laatste {w.tempo.basis_bestanden} bestanden)</span>
            </div>
          )}
          {w.huidig && (
            <div className="text-xs text-slate-600 truncate" data-testid="wd-huidig">
              <CloudDownload className="h-3.5 w-3.5 inline mr-1 text-slate-400" />
              {w.huidig.status === "downloaden" ? `Downloaden ${huidigPct ?? 0}%` : "Verwerken"}: <span className="font-medium">{w.huidig.naam?.split("/").pop()}</span>
              {w.volgende && <span className="text-slate-400"> · volgende al aan het downloaden ({w.volgende.bytes ? Math.round(((w.volgende.gedownload || 0) / w.volgende.bytes) * 100) : 0}%)</span>}
            </div>
          )}
          <div className="text-[11px] text-slate-400">Laatste activiteit {geleden(w.heartbeat)}{w.aantal_fout ? <span className="text-rose-600 ml-2">· {w.aantal_fout} bestand(en) mislukt</span> : null}</div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[["Mails verwerkt", t.verwerkt], ["Nieuw", t.nieuw], ["Bijlagen", t.bijlagen]].map(([l, v]) => (
            <div key={l} className="rounded-lg bg-slate-50 border border-border px-2.5 py-2" data-testid={`wd-teller-${l.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="text-lg font-bold tabular-nums leading-none">{n(v)}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{l}</div>
            </div>
          ))}
          {[["Debiteuren", "gekoppeld_debiteuren"], ["Relaties", "gekoppeld_relaties"], ["Klachten", "gekoppeld_klachten"]].map(([l, k]) => (
            <div key={k} className="rounded-lg bg-indigo-50/60 border border-indigo-100 px-2.5 py-2" title="Direct bij het importeren gekoppeld" data-testid={`wd-teller-${k.replace(/_/g, "-")}`}>
              <div className="text-lg font-bold tabular-nums leading-none text-indigo-900">{n((t[k] || 0) + (dm?.[k] || 0))}</div>
              <div className="text-[10px] uppercase tracking-wider text-indigo-700/80 mt-1">↔ {l}</div>
            </div>
          ))}
          <div className="col-span-3 text-[11px] text-slate-500">
            Archief totaal: <b>{n(d.archief_mails)}</b> mails · mails worden tijdens de import direct gekoppeld
            {dm?.status === "bezig" && <span className="text-indigo-700" data-testid="wd-directe-match-bezig"> · inhaalslag eerdere mails: {n(dm.verwerkt)} van {n(dm.todo_bij_start)}</span>}
            {w.status === "klaar" && match ? <> · volledige match: <b>{match.status}</b>{match.fase ? ` (${match.fase})` : ""}</> : null}
          </div>
        </div>
      </div>

      {w.status === "fout" && <div className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2.5" data-testid="wd-fout">{w.fout}</div>}
      {w.status === "gepauzeerd" && w.fout && <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">{w.fout}</div>}
      {w.status === "klaar" && (
        <div className="mt-3 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 flex flex-wrap items-center justify-between gap-2" data-testid="wd-klaar">
          <span>Alle bestanden zijn verwerkt{w.fase === "koppelen" ? " — debiteuren koppelen loopt nog" : ""}. Vergeet niet de <b>noodstop</b> pas uit te zetten als de volledige match klaar is.</span>
          <Link to="/mail-dashboard" className="font-semibold underline underline-offset-2" data-testid="wd-naar-mail">Naar Mail-dashboard →</Link>
        </div>
      )}
    </div>
  );
}

export default WorkdriveImportKaart;
